import axios from 'axios';
import * as types from '../mutation-types';
import {performApiRequest, performApiRequestRaw} from '@/store/helpers/vuex-helpers';
import type {DocumentsMeta, OrderDocumentType, OrderLine, OrderType} from '@/types/order';
import {d} from '@/utils/consoleHelper.ts';
import {handleApiError} from '@/utils/errorHandler.ts';
import type {SortItem} from '@/store/modules/booking.ts';
import {createOrderUrlQueryString} from '@/utils/urls.ts';
import type {ActionContext, Module} from 'vuex';
import type {RootState} from '@/types/RootState';
import type {StoreLoaders} from '@/types/common';

export type DocumentQueryOptions = {
    page?: number;
    sortDesc?: string | boolean | boolean[];
    itemsPerPage?: number;
    searchTerm?: string;
    searchTopic?: string;
    exporting?: boolean;
    providers?: string[];
    statuses?: string[];
    filter?: Record<string, unknown>;
    sortBy?: SortItem[];
    document_type?: string;
};

export type vTableOptions = {
    groupBy: string[];
    page: number;
    itemsPerPage: number;
    sortBy: SortItem[];
    search: string | undefined;
};

export type OrderLoadingState = StoreLoaders & {
    newOrder: boolean;
    update: boolean;
    cancelOrder: boolean;
    reserving: boolean;
    concept: boolean;
    deleteOrderDocument: boolean;
    updateLine: boolean;
    addLine: boolean;
    removeLine: boolean;
    holdDeposit: boolean;
    addCustomerToOrder: boolean;
    removeCustomer: boolean;
    getDocuments: boolean;
    updateLinePositions: boolean;
};

export type OrderDialogs = {
    pricingWizard: boolean;
    deliveryLocations: boolean;
    listingExtras: boolean;
    orderExport: boolean;
};

export type OrderLineAvailability = {
    available: number;
    plannable: number;
    stock_count: number;
};

export type OrderAvailabilityMap = Record<string, OrderLineAvailability>;

export type OrderDocumentDownloadStatus = {
    url?: string;
    done?: boolean;
    document?: OrderDocumentType;
    status?: Record<string, unknown>;
} | null;

export type OrderDocumentDownloadResponse = {
    url?: string;
    done?: boolean;
    document: OrderDocumentType;
    status?: Record<string, unknown>;
};

export interface OrderState {
    loading: OrderLoadingState;
    order: OrderType | null;
    /** Bumped after every order mutation; panels watch it to refresh. */
    orderVersion: number;
    documents: OrderDocumentType[];
    documentsMeta: DocumentsMeta;
    orderDocumentsTableOptions: vTableOptions;
    document: OrderDocumentType | null;
    downloadStatus: OrderDocumentDownloadStatus;
    availability: OrderAvailabilityMap;
    currentWorkingPricingWizardOrderLine: OrderLine | null;
    dialogs: OrderDialogs;
    lastCreatedQuoteDocument: OrderDocumentType | null;
}

export interface UpdateOrderPayload {
    orderUuid: string;
    attributes?: Record<string, unknown>;
}

export type OrderCustomerPayload = {
    orderUuid: string;
    r_id: string;
};

export type UpdateOrderLinePayload = {
    line: string;
    attributes: Record<string, unknown>;
};

export type UpdateOrderLinePositionsPayload = {
    orderUuid: string;
    positions: Array<{ line: string; position: number }>;
};

export type AddLinePayload = {
    orderUuid: string;
    line: OrderLine;
};

export type HoldOrderDepositPayload = {
    orderUuid: string;
    attributes: Record<string, unknown>;
};

export type CreateQuotePayload = {
    attributes: { order_id?: string };
};

export type UpdateOrderDocumentPayload = {
    documentUuid: string;
    attributes: Record<string, unknown>;
};

export type OrderResponse = {
    order: OrderType;
};

export type OrderAvailabilityResponse = {
    available: OrderAvailabilityMap;
};

export type PaginatedOrderDocumentsResponse = {
    data: OrderDocumentType[];
    meta: DocumentsMeta;
};

export type OrderDocumentResponse = {
    document: OrderDocumentType;
};

const state: OrderState = {
    loading: {
        newOrder: false,
        update: false,
        cancelOrder: false,
        reserving: false,
        concept: false,
        deleteOrderDocument: false,
        updateLine: false,
        addLine: false,
        removeLine: false,
        holdDeposit: false,
        addCustomerToOrder: false,
        removeCustomer: false,
        getDocuments: false,
        updateLinePositions: false,
    },
    dialogs: {
        pricingWizard: false,
        deliveryLocations: false,
        listingExtras: false,
        orderExport: false,
    },
    currentWorkingPricingWizardOrderLine: null,
    order: null,
    orderVersion: 0,
    documents: [],
    documentsMeta: {
        current_page: 1,
        from: 1,
        last_page: 1,
        links: [],
        path: '',
        per_page: 25,
        to: 1,
        total: 0,
    },
    orderDocumentsTableOptions: {
        groupBy: [],
        page: 1,
        itemsPerPage: 25,
        sortBy: [{'key': 'created_at', 'order': 'desc'}],
        search: undefined,
    },
    document: null,
    downloadStatus: null,
    availability: {},
    lastCreatedQuoteDocument: null,
};

const getters = {
    order: (state: OrderState) => state.order,
    orderVersion: (state: OrderState) => state.orderVersion,
    documents: (state: OrderState) => state.documents,
    documentsMeta: (state: OrderState) => state.documentsMeta,
    orderDocumentsTableOptions: (state: OrderState) => state.orderDocumentsTableOptions,
    document: (state: OrderState) => state.document,
    loading: (state: OrderState) => state.loading,
    downloadStatus: (state: OrderState) => state.downloadStatus,
    availability: (state: OrderState) => state.availability,
    orderDialogs: (state: OrderState) => state.dialogs,
    currentWorkingPricingWizardOrderLine: (state: OrderState) => state.currentWorkingPricingWizardOrderLine,
    lastCreatedQuoteDocument: (state: OrderState) => state.lastCreatedQuoteDocument,
};

const mutations = {
    [types.SET_ORDERS_LOADING](state: OrderState, loading: { subject: string; loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_ORDER](state: OrderState, order: OrderType | null) {
        d.badge('SET_ORDER');
        state.order = order;
    },
    [types.TOUCH_ORDER](state: OrderState) {
        state.orderVersion += 1;
    },
    [types.SET_ORDER_DOCUMENTS](state: OrderState, documents: OrderDocumentType[]) {
        state.documents = documents;
    },
    [types.SET_ORDER_DOCUMENTS_META](state: OrderState, meta: DocumentsMeta) {
        state.documentsMeta = meta;
    },
    [types.SET_ORDER_DOCUMENTS_TABLE_OPTIONS](state: OrderState, payload: vTableOptions) {
        state.orderDocumentsTableOptions = payload;
    },
    [types.SET_ORDER_DOCUMENT](state: OrderState, document: OrderDocumentType | null) {
        state.document = document;
    },
    [types.SET_ORDER_DOCUMENT_DOWNLOAD_STATUS](state: OrderState, status: OrderDocumentDownloadResponse | null) {
        state.downloadStatus = status;
    },
    [types.SET_ORDER_AVAILABILITY](state: OrderState, availability: OrderAvailabilityMap) {
        state.availability = availability;
    },
    [types.SET_OPEN_PRICING_WIZARD_DIALOG](state: OrderState, data: { open: boolean; lineItem: OrderLine }) {
        state.currentWorkingPricingWizardOrderLine = data.lineItem;
        state.dialogs.pricingWizard = data.open;
    },
    [types.SET_DELIVERY_LOCATIONS_DIALOG](state: OrderState, open: boolean) {
        state.dialogs.deliveryLocations = open;
    },
    [types.SET_ORDER_EXPORT_DIALOG](state: OrderState, open: boolean) {
        state.dialogs.orderExport = open;
    },
    [types.SET_LISTING_EXTRAS_DIALOG](state: OrderState, open: boolean) {
        state.dialogs.listingExtras = open;
    },
    [types.SET_LAST_CREATED_QUOTE_DOCUMENT](state: OrderState, document: OrderDocumentType | null) {
        state.lastCreatedQuoteDocument = document;
    },
};

const actions = {
    async newOrder({commit}: ActionContext<OrderState, RootState>) {
        try {
            const responseData = await performApiRequest<OrderResponse>(
                commit,
                'SET_ORDERS_LOADING',
                'newOrder',
                () => axios.get('/v1/orders/drafts/latest'),
            );

            commit(types.SET_ORDER, responseData.order);
        } catch (error) {
            d.error('Error creating new order:', error);
        }
    },

    async getOrder({commit}: ActionContext<OrderState, RootState>, orderUuid: string): Promise<void> {
        try {
            const responseData = await performApiRequest<OrderResponse>(
                commit,
                'SET_ORDERS_LOADING',
                'getOrder',
                () => axios.get(`/v1/orders/${orderUuid}`),
            );

            commit(types.SET_ORDER, responseData.order);
        } catch (error) {
            handleApiError(error);
        }
    },

    async update({commit}: ActionContext<OrderState, RootState>, {orderUuid, attributes = {}}: UpdateOrderPayload) {
        try {
            const responseData = await performApiRequest<OrderResponse>(
                commit,
                'SET_ORDERS_LOADING',
                'update',
                () => axios.put(`/v1/orders/${orderUuid}`, {attributes}),
            );

            commit(types.SET_ORDER, responseData.order);
            commit(types.TOUCH_ORDER);

            return responseData.order;
        } catch (error) {
            handleApiError(error);

            return undefined;
        }
    },

    async makeCustomerPrimary({commit}: ActionContext<OrderState, RootState>, {orderUuid, r_id}: OrderCustomerPayload) {
        try {
            const responseData = await performApiRequest<OrderResponse>(
                commit,
                'SET_ORDERS_LOADING',
                'update',
                () => axios.patch(`/v1/orders/${orderUuid}/customers/${r_id}/primary`),
            );

            commit(types.SET_ORDER, responseData.order);
            commit(types.TOUCH_ORDER);
        } catch (error) {
            d.error('Error creating new order:', error);
        }
    },

    async removeCustomer({commit}: ActionContext<OrderState, RootState>, {orderUuid, r_id}: OrderCustomerPayload) {
        const responseData = await performApiRequest<OrderResponse>(
            commit,
            'SET_ORDERS_LOADING',
            'removeCustomer',
            () => axios.delete(`/v1/orders/${orderUuid}/customers/${r_id}`),
        );

        commit(types.SET_ORDER, responseData.order);
        commit(types.TOUCH_ORDER);
    },

    async addCustomerToOrder({commit}: ActionContext<OrderState, RootState>, {orderUuid, r_id}: OrderCustomerPayload) {
        const responseData = await performApiRequest<OrderResponse>(
            commit,
            'SET_ORDERS_LOADING',
            'addCustomerToOrder',
            () => axios.post(`/v1/orders/${orderUuid}/customers`, {r_id}),
        );

        commit(types.SET_ORDER, responseData.order);
        commit(types.TOUCH_ORDER);

        return responseData.order;
    },

    async reserve({commit}: ActionContext<OrderState, RootState>, payload: { orderUuid: string; notifications?: Record<string, boolean> }) {
        const responseData = await performApiRequest<OrderResponse>(
            commit,
            'SET_ORDERS_LOADING',
            'reserving',
            () => axios.post(`/v1/orders/${payload.orderUuid}/reservations`, {
                notifications: payload.notifications,
            }),
        );

        commit(types.SET_ORDER, responseData.order);
        commit(types.TOUCH_ORDER);

        return responseData;
    },

    async concept({commit}: ActionContext<OrderState, RootState>, orderUuid: string) {
        try {
            const responseData = await performApiRequest<OrderResponse>(
                commit,
                'SET_ORDERS_LOADING',
                'concept',
                () => axios.post(`/v1/orders/${orderUuid}/draft`),
            );
            commit(types.SET_ORDER, responseData.order);
            commit(types.TOUCH_ORDER);

            return responseData;
        } catch (error) {
            handleApiError(error);

            return undefined;
        }
    },

    async availability({commit}: ActionContext<OrderState, RootState>, orderUuid: string) {
        const responseData = await performApiRequest<OrderAvailabilityResponse>(
            commit,
            'SET_ORDERS_LOADING',
            'availability',
            () => axios.get(`/v1/orders/${orderUuid}/availability`),
        );

        commit(types.SET_ORDER_AVAILABILITY, responseData.available ?? {});
    },

    async holdOrderDeposit({commit}: ActionContext<OrderState, RootState>, {orderUuid, attributes}: HoldOrderDepositPayload) {
        await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'holdDeposit',
            () => axios.post(`/v1/orders/${orderUuid}/deposits/hold`, attributes),
        );
        commit(types.TOUCH_ORDER);

        return true;
    },

    async createNewQuote({commit}: ActionContext<OrderState, RootState>, attributes: CreateQuotePayload) {
        const responseData = await performApiRequest<OrderDocumentResponse>(
            commit,
            'SET_ORDERS_LOADING',
            'update',
            () => axios.post('/v1/quotes', attributes),
        );

        commit(types.SET_LAST_CREATED_QUOTE_DOCUMENT, responseData?.document ?? null);
    },

    async getAllOrderDocuments({commit}: ActionContext<OrderState, RootState>, options: DocumentQueryOptions = {}) {
        const data = {
            page: options.page || 1,
            sortDesc: options.sortDesc || false,
            itemsPerPage: options.itemsPerPage || 25,
            searchTerm: options.searchTerm || '',
            searchTopic: options.searchTopic || '',
            exporting: options.exporting || false,
            document_type: options.document_type,
        };

        const query = createOrderUrlQueryString(data, true);

        const responseData = await performApiRequest<PaginatedOrderDocumentsResponse>(
            commit,
            'SET_ORDERS_LOADING',
            'getDocuments',
            () => axios.get(`/v1/order-documents${query}`),
        );

        commit(types.SET_ORDER_DOCUMENTS, responseData?.data ?? []);
        commit(types.SET_ORDER_DOCUMENTS_META, responseData?.meta);
    },

    async getOrderDocuments({commit}: ActionContext<OrderState, RootState>, orderUuid: string) {
        const responseData = await performApiRequest<{ data: OrderDocumentType[] }>(
            commit,
            'SET_ORDERS_LOADING',
            'getDocuments',
            () => axios.get(`/v1/orders/${orderUuid}/documents`),
        );

        commit(types.SET_ORDER_DOCUMENTS, responseData?.data ?? []);
    },

    async getOrderDocument({commit}: ActionContext<OrderState, RootState>, documentUuid: string) {
        try {
            const responseData = await performApiRequest<OrderDocumentResponse>(
                commit,
                'SET_ORDERS_LOADING',
                'getDocuments',
                () => axios.get(`/v1/order-documents/${documentUuid}`),
            );

            commit(types.SET_ORDER_DOCUMENT, responseData?.document ?? null);

            return responseData?.document;
        } catch (error) {
            handleApiError(error);
        }
    },

    updateOrderDocument({commit}: ActionContext<OrderState, RootState>, {documentUuid, attributes}: UpdateOrderDocumentPayload) {
        commit(types.SET_ORDERS_LOADING, {subject: 'update', loading: true});

        return axios
            .put(`/v1/order-documents/${documentUuid}`, {attributes})
            .then((response) => {
                commit(types.SET_ORDER_DOCUMENT, response?.data?.document ?? null);

                return {document: response?.data?.document};
            })
            .catch((error) => {
                handleApiError(error);

                return {error: error.response};
            })
            .finally(() => {
                commit(types.SET_ORDERS_LOADING, {subject: 'update', loading: false});
            });
    },

    async deleteOrderDocument({commit}: ActionContext<OrderState, RootState>, documentUuid: string) {
        return await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'deleteOrderDocument',
            () => axios.delete(`/v1/order-documents/${documentUuid}`),
        );
    },

    async downloadOrderDocument(
        {commit}: ActionContext<OrderState, RootState>,
        payload: string | { documentUuid: string; regenerate?: boolean },
    ) {
        const documentUuid = typeof payload === 'string' ? payload : payload.documentUuid;
        const regenerate = typeof payload === 'string' ? false : Boolean(payload.regenerate);

        const responseData = await performApiRequest<OrderDocumentDownloadResponse>(
            commit,
            'SET_ORDERS_LOADING',
            'update',
            () => axios.get(`/v1/order-documents/${documentUuid}/download`, {
                params: regenerate ? {regenerate: 1} : undefined,
            }),
        );

        commit(types.SET_ORDER_DOCUMENT_DOWNLOAD_STATUS, responseData);

        return responseData;
    },

    /**
     * Fetch the rendered PDF as a file so the browser saves it instead of
     * opening its own viewer. Resolves with the blob and the filename the
     * server chose.
     */
    async downloadOrderDocumentFile(
        {commit}: ActionContext<OrderState, RootState>,
        payload: { documentUuid: string; regenerate?: boolean },
    ): Promise<{ blob: Blob; filename: string }> {
        const response = await performApiRequestRaw<Blob>(
            commit,
            'SET_ORDERS_LOADING',
            'update',
            () => axios.get(`/v1/order-documents/${payload.documentUuid}/file`, {
                params: payload.regenerate ? {regenerate: 1} : undefined,
                responseType: 'blob',
            }),
        );

        const disposition = String(response.headers['content-disposition'] ?? '');
        const match = /filename="?([^";]+)"?/i.exec(disposition);

        return {
            blob: response.data,
            filename: match?.[1] ?? 'document.pdf',
        };
    },

    async addLine({commit}: ActionContext<OrderState, RootState>, {orderUuid, line}: AddLinePayload) {
        try {
            const responseData = await performApiRequest<OrderResponse>(
                commit,
                'SET_ORDERS_LOADING',
                'addLine',
                () => axios.post(`/v1/orders/${orderUuid}/line`, {line}),
            );
            commit(types.SET_ORDER, responseData.order);
            commit(types.TOUCH_ORDER);

            return responseData.order;
        } catch (error) {
            handleApiError(error);

            return undefined;
        }
    },

    async updateLine({commit}: ActionContext<OrderState, RootState>, {line, attributes}: UpdateOrderLinePayload) {
        const responseData = await performApiRequest<OrderResponse>(
            commit,
            'SET_ORDERS_LOADING',
            'updateLine',
            () => axios.put(`/v1/line/${line}`, {attributes}),
        );

        commit(types.SET_ORDER, responseData.order);
        commit(types.TOUCH_ORDER);

        return responseData.order;
    },

    async updateLines({commit}: ActionContext<OrderState, RootState>, lines: OrderLine[]) {
        await performApiRequest(
            commit,
            'SET_ORDERS_LOADING',
            'updateLine',
            () => axios.put('/v1/lines', {lines}),
        );
    },

    async updateLinePositions({commit}: ActionContext<OrderState, RootState>, {orderUuid, positions}: UpdateOrderLinePositionsPayload) {
        try {
            const responseData = await performApiRequest<OrderResponse>(
                commit,
                'SET_ORDERS_LOADING',
                'updateLinePositions',
                () => axios.put(`/v1/orders/${orderUuid}/lines/positions`, {positions}),
            );

            commit(types.SET_ORDER, responseData.order);
            commit(types.TOUCH_ORDER);

            return responseData.order;
        } catch (error) {
            // Validation / conflict while dragging lines (VUE-9K0) — toast, don't reject.
            handleApiError(error);

            return null;
        }
    },

    async removeLine({commit}: ActionContext<OrderState, RootState>, line: string) {
        try {
            await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'removeLine',
                () => axios.delete(`/v1/line/${line}`),
            );
            commit(types.TOUCH_ORDER);

            return true;
        } catch (error) {
            handleApiError(error);

            return false;
        }
    },

    getOrderItemModel({commit}: ActionContext<OrderState, RootState>, data: { model: string; id: string }) {
        commit(types.SET_ORDERS_LOADING, {subject: 'itemModel', loading: true});

        return axios
            .get(`/v1/line-item-model/${data.model}/${data.id}`)
            .then((response) => {
                return response?.data?.model;
            })
            .catch(() => {
                return undefined;
            })
            .finally(() => {
                commit(types.SET_ORDERS_LOADING, {subject: 'itemModel', loading: false});
            });
    },

    async cancelOrder({commit}: ActionContext<OrderState, RootState>, orderUuid: string) {
        try {
            await performApiRequest(
                commit,
                'SET_ORDERS_LOADING',
                'cancelOrder',
                () => axios.post(`/v1/orders/${orderUuid}/cancel`),
            );

            commit(types.SET_ORDER, null);
        } catch (error) {
            handleApiError(error);
        }
    },

    openDeliveryLocationsDialog({commit}: ActionContext<OrderState, RootState>) {
        commit(types.SET_DELIVERY_LOCATIONS_DIALOG, true);
    },

    closeDeliveryLocationsDialog({commit}: ActionContext<OrderState, RootState>) {
        commit(types.SET_DELIVERY_LOCATIONS_DIALOG, false);
    },

    openListingExtrasDialog({commit}: ActionContext<OrderState, RootState>) {
        commit(types.SET_LISTING_EXTRAS_DIALOG, true);
    },

    closeListingExtrasDialog({commit}: ActionContext<OrderState, RootState>) {
        commit(types.SET_LISTING_EXTRAS_DIALOG, false);
    },
};

const orderModule: Module<OrderState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};

export default orderModule;
